import { useState, useEffect, useCallback, useMemo } from "react";
import { useTheme } from "../context/Themecontext";
import Api from "../api/Api";
import { Select } from "antd";

const ITEMS_PER_PAGE = 8;

export default function GerarProvasPage() {
  const { dark } = useTheme();

  // Dados principais
  const [questionarios, setQuestionarios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [formacoes, setFormacoes] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  // Filtros da lista
  const [filterUsuario, setFilterUsuario] = useState("");
  const [filterFormacao, setFilterFormacao] = useState("");
  const [page, setPage] = useState(1);

  // Modal e seleção
  const [showModal, setShowModal] = useState(false);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState("");
  const [selectedFormacaoId, setSelectedFormacaoId] = useState("");
 

  // Ação
  const [gerando, setGerando] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // ══════════ Tokens de cor ══════════
  const t = dark
    ? {
        bg: "#080c14",
        bgCard: "#111827",
        bgHover: "rgba(0,144,231,0.08)",
        bgActive: "rgba(0,144,231,0.16)",
        bgInput: "#1a2232",
        border: "rgba(0,174,255,0.12)",
        borderActive: "rgba(0,174,255,0.35)",
        textPrimary: "#eef5ff",
        textSecondary: "#b0c7e0",
        textMuted: "#7b93b0",
        textActive: "#00b4ff",
        shadow: "0 12px 40px rgba(0,0,0,0.8)",
        rowHover: "rgba(0,144,231,0.06)",
        success: "#22c55e",
        successBg: "rgba(34,197,94,0.12)",
        warning: "#f59e0b",
        warningBg: "rgba(245,158,11,0.12)",
        danger: "#ef4444",
        dangerBg: "rgba(239,68,68,0.12)",
        overlay: "rgba(8,12,20,0.8)",
      }
    : {
        bg: "#f4f7fc",
        bgCard: "#ffffff",
        bgHover: "#f0f6ff",
        bgActive: "#e6f0ff",
        bgInput: "#fafbfd",
        border: "#e2e8f0",
        borderActive: "rgba(0,100,230,0.4)",
        textPrimary: "#0f1a2e",
        textSecondary: "#475569",
        textMuted: "#8b9eb0",
        textActive: "#0057ff",
        shadow: "0 8px 30px rgba(0,50,150,0.06)",
        rowHover: "#f8faff",
        success: "#16a34a",
        successBg: "#f0fdf4",
        warning: "#d97706",
        warningBg: "#fffbeb",
        danger: "#ef4444",
        dangerBg: "#fef2f2",
        overlay: "rgba(15,23,42,0.4)",
      };

  // ── Carregar todos os dados ──
  const loadAll = useCallback(async () => {
    setLoadingData(true);
    setErrorMsg("");
    try {
      const [resQ, resU, resF] = await Promise.all([
        Api.getQuestionariosUsers(),
        Api.getUser(),
        Api.getFormacao(),
      ]);

      const listaQuestionarios = Array.isArray(resQ?.data)
        ? resQ.data
        : Array.isArray(resQ)
        ? resQ
        : [];
      const listaUsuarios = Array.isArray(resU?.data)
        ? resU.data
        : Array.isArray(resU)
        ? resU
        : [];
      const listaFormacoes = Array.isArray(resF?.data)
        ? resF.data
        : Array.isArray(resF)
        ? resF
        : [];

      // Filtra utilizadores ativos, se houver algum ativo
      const ativos = listaUsuarios.filter(
        (u) => u.status === "1" || u.status === 1
      );
      setUsuarios(ativos.length > 0 ? ativos : listaUsuarios);
      setFormacoes(listaFormacoes);
      setQuestionarios(listaQuestionarios);
    } catch {
      setErrorMsg("Erro ao carregar dados iniciais.");
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Reset da página ao alterar filtros
  useEffect(() => {
    setPage(1);
  }, [filterUsuario, filterFormacao]);

  // ── Filtragem e paginação ──
  const filteredQuestionarios = useMemo(() => {
    return questionarios.filter((q) => {
      const matchUsuario =
        filterUsuario.trim() === "" ||
        (q.nome || "").toLowerCase().includes(filterUsuario.trim().toLowerCase());
      const matchFormacao =
        filterFormacao.trim() === "" ||
        (q.formacao || "").toLowerCase().includes(filterFormacao.trim().toLowerCase());
      return matchUsuario && matchFormacao;
    });
  }, [questionarios, filterUsuario, filterFormacao]);

  const totalPages = Math.ceil(filteredQuestionarios.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filteredQuestionarios.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredQuestionarios, safePage]);

  // ── Abrir / fechar modal ──
  const openModal = () => {
    setSelectedUsuarioId("");
    setSelectedFormacaoId("");
 
    setErrorMsg("");
    setSuccessMsg("");
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setGerando(false);
  };

  // ── Gerar questionário ──
  const handleGerarQuestionario = async () => {
    if (!selectedUsuarioId || !selectedFormacaoId) {
      setErrorMsg("Selecione um utilizador e uma formação.");
      return;
    }
    setErrorMsg("");
    setSuccessMsg("");
    setGerando(true);
    try {
      const res = await Api.criarQuestionario({
        id_formacao: selectedFormacaoId,
        id_user: selectedUsuarioId,
      });

      if (res?.success === false) {
        setErrorMsg(res.message || "Erro ao gerar questionário.");
        setGerando(false);
        return;
      }

      setSuccessMsg("Questionário gerado com sucesso!");
      setGerando(false);
      // Recarrega a lista
      const resQ = await Api.getQuestionariosUsers();
      const lista = Array.isArray(resQ?.data)
        ? resQ.data
        : Array.isArray(resQ)
        ? resQ
        : [];
      setQuestionarios(lista);
      setShowModal(false);
    } catch {
      setErrorMsg("Erro ao gerar questionário.");
      setGerando(false);
    }
  };

 

  const formatDate = (data) => {
    if (!data) return "—";
    try {
      return new Date(data).toLocaleString("pt-PT");
    } catch {
      return data;
    }
  };

  // ═══════════════ Estilos ═══════════════
  const styles = `
    .gp-page { font-family: 'Inter', system-ui, sans-serif; background: ${t.bg}; min-height: 100vh; padding: 32px 20px; }
    .gp-container { max-width: 1200px; margin: 0 auto; }
    .gp-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 32px; flex-wrap: wrap; }
    .gp-heading { font-size: 1.65rem; font-weight: 700; letter-spacing: -0.03em; color: ${t.textPrimary}; margin-bottom: 4px; }
    .gp-sub { font-size: 0.88rem; color: ${t.textMuted}; font-weight: 400; }

    .gp-btn-add {
      display: inline-flex; align-items: center; gap: 8px; height: 44px; padding: 0 24px;
      border-radius: 14px; border: none; background: linear-gradient(135deg, #00aaff, #0044ee);
      color: #fff; font-weight: 600; font-size: 0.86rem; cursor: pointer;
      box-shadow: 0 6px 18px rgba(0,100,255,0.3); transition: all 0.25s; letter-spacing: -0.01em;
      white-space: nowrap;
    }
    .gp-btn-add:hover { transform: translateY(-2px); box-shadow: 0 12px 26px rgba(0,100,255,0.45); }

    .gp-toolbar { display: flex; gap: 12px; margin-bottom: 24px; flex-wrap: wrap; }
    .gp-search-box {
      display: flex; align-items: center; gap: 8px; background: ${t.bgCard};
      border: 1px solid ${t.border}; border-radius: 14px; padding: 0 16px; height: 44px;
      flex: 1 1 240px; min-width: 200px; transition: border 0.2s, box-shadow 0.2s;
    }
    .gp-search-box:focus-within { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.18); }
    .gp-search-box input { background: none; border: none; outline: none; font-size: 0.86rem; color: ${t.textPrimary}; width: 100%; }
    .gp-search-box input::placeholder { color: ${t.textMuted}; }

    .gp-error-banner { background: ${t.dangerBg}; color: ${t.danger}; padding: 14px 20px; border-radius: 14px; margin-bottom: 24px; }
    .gp-success-banner { background: ${t.successBg}; color: ${t.success}; padding: 14px 20px; border-radius: 14px; margin-bottom: 24px; }

    .gp-table-card { background: ${t.bgCard}; border: 1px solid ${t.border}; border-radius: 18px; overflow: hidden; }
    .gp-table { width: 100%; border-collapse: collapse; }
    .gp-table thead th { text-align: left; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${t.textMuted}; padding: 14px 22px; background: ${t.bgHover}; border-bottom: 1px solid ${t.border}; }
    .gp-table tbody td { padding: 14px 22px; font-size: 0.88rem; color: ${t.textPrimary}; border-bottom: 1px solid ${t.border}; vertical-align: middle; }
    .gp-table tbody tr:last-child td { border-bottom: none; }
    .gp-table tbody tr:hover { background: ${t.rowHover}; }

    .gp-badge { display: inline-flex; align-items: center; padding: 4px 12px; border-radius: 999px; font-size: 0.72rem; font-weight: 700; }
    .gp-badge.pendente { background: ${t.warningBg}; color: ${t.warning}; }
    .gp-badge.concluido { background: ${t.successBg}; color: ${t.success}; }
    .gp-badge.emcurso { background: ${t.bgActive}; color: ${t.textActive}; }

    .gp-pagination { display: flex; align-items: center; justify-content: space-between; padding: 16px 22px; border-top: 1px solid ${t.border}; color: ${t.textSecondary}; font-size: 0.84rem; }
    .gp-pagination-controls { display: flex; align-items: center; gap: 4px; }
    .gp-pagination-btn { width: 34px; height: 34px; border-radius: 8px; border: 1px solid ${t.border}; background: ${t.bgCard}; display: flex; align-items: center; justify-content: center; cursor: pointer; color: ${t.textSecondary}; transition: all 0.2s; }
    .gp-pagination-btn:hover:not(:disabled) { background: ${t.bgHover}; color: ${t.textActive}; }
    .gp-pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
    .gp-pagination-num { width: 34px; height: 34px; border-radius: 8px; border: none; background: transparent; color: ${t.textSecondary}; cursor: pointer; font-weight: 500; }
    .gp-pagination-num.active { background: ${t.bgActive}; color: ${t.textActive}; font-weight: 700; }

    /* Modal */
    .gp-overlay { position: fixed; inset: 0; background: ${t.overlay}; backdrop-filter: blur(6px); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 20px; }
    .gp-modal { background: ${t.bgInput}; border: 1px solid ${t.border}; border-radius: 22px; box-shadow: ${t.shadow}; width: 100%; max-width: 500px; }
    .gp-modal-header { padding: 24px 28px 20px; border-bottom: 1px solid ${t.border}; display: flex; align-items: center; justify-content: space-between; }
    .gp-modal-title { font-size: 1.15rem; font-weight: 700; color: ${t.textPrimary}; }
    .gp-modal-close { width: 32px; height: 32px; border-radius: 10px; border: none; background: ${t.bgHover}; color: ${t.textSecondary}; display: flex; align-items: center; justify-content: center; cursor: pointer; }
    .gp-modal-close:hover { background: ${t.bgActive}; color: ${t.textActive}; }
    .gp-modal-body { padding: 24px 28px; display: flex; flex-direction: column; gap: 16px; }
    .gp-modal-footer { padding: 20px 28px 24px; display: flex; gap: 14px; justify-content: flex-end; border-top: 1px solid ${t.border}; }
    .gp-btn-secondary { height: 42px; padding: 0 22px; border-radius: 12px; border: 1px solid ${t.border}; background: transparent; color: ${t.textSecondary}; font-weight: 600; font-size: 0.84rem; cursor: pointer; }
    .gp-btn-secondary:hover { background: ${t.bgHover}; color: ${t.textPrimary}; }
    .gp-btn-primary { height: 42px; padding: 0 24px; border-radius: 12px; border: none; background: linear-gradient(135deg, #00aaff, #0044ee); color: #fff; font-weight: 600; font-size: 0.84rem; cursor: pointer; display: flex; align-items: center; gap: 8px; }
    .gp-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

    .gp-field { display: flex; flex-direction: column; gap: 6px; }
    .gp-field label { font-size: 0.78rem; font-weight: 600; color: ${t.textSecondary}; text-transform: uppercase; letter-spacing: 0.05em; }
    .gp-input, .gp-select { height: 44px; border-radius: 12px; border: 1px solid ${t.border}; background: ${t.bgInput}; color: ${t.textPrimary}; padding: 0 16px; font-size: 0.86rem; outline: none; width: 100%; box-sizing: border-box; }
    .gp-input:focus, .gp-select:focus { border-color: #0090e7; box-shadow: 0 0 0 3px rgba(0,144,231,0.15); }
    .gp-select { appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 12px center; cursor: pointer; }
/* Ant Design Select */
.gp-ant-select {
  width: 100%;
}

.gp-ant-select .ant-select-selector {
  height: 44px !important;
  min-height: 44px !important;
  border-radius: 12px !important;
  border: 1px solid ${t.border} !important;
  background: ${t.bgInput} !important;
  color: ${t.textPrimary} !important;
  box-shadow: none !important;
  padding: 0 16px !important;
  display: flex !important;
  align-items: center !important;
  transition: all 0.2s ease;
}

.gp-ant-select:hover .ant-select-selector {
  border-color: ${t.borderActive} !important;
}

.gp-ant-select.ant-select-focused .ant-select-selector {
  border-color: #0090e7 !important;
  box-shadow: 0 0 0 3px rgba(0,144,231,0.15) !important;
}

.gp-ant-select .ant-select-selection-placeholder {
  color: ${t.textMuted} !important;
  font-size: 0.86rem;
}

.gp-ant-select .ant-select-selection-item {
  color: ${t.textPrimary} !important;
  font-size: 0.86rem;
}

.gp-ant-select .ant-select-arrow {
  color: ${t.textMuted} !important;
}

.gp-ant-select .ant-select-clear {
  background: ${t.bgInput} !important;
  color: ${t.textMuted} !important;
}

/* Dropdown */
.gp-ant-select-dropdown {
  background: ${t.bgCard} !important;
  border: 1px solid ${t.border} !important;
  border-radius: 12px !important;
  box-shadow: ${t.shadow} !important;
  padding: 6px !important;
}

.gp-ant-select-dropdown .ant-select-item {
  color: ${t.textPrimary} !important;
  border-radius: 8px !important;
  font-size: 0.86rem;
  padding: 9px 12px !important;
}

.gp-ant-select-dropdown .ant-select-item-option-active {
  background: ${t.bgHover} !important;
}

.gp-ant-select-dropdown .ant-select-item-option-selected {
  background: ${t.bgActive} !important;
  color: ${t.textActive} !important;
  font-weight: 600;
}

.gp-ant-select-dropdown .ant-select-item-option-selected .ant-select-item-option-state {
  color: ${t.textActive} !important;
}



    .mini-spinner { width: 16px; height: 16px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; animation: spin 0.7s linear infinite; display: inline-block; }
    @keyframes spin { to { transform: rotate(360deg); } }

    @media (max-width: 640px) {
      .gp-toolbar { flex-direction: column; }
      .gp-table thead { display: none; }
      .gp-table tbody tr { display: flex; flex-direction: column; padding: 16px; }
      .gp-table tbody td { padding: 0; border-bottom: none; }
      .gp-pagination { flex-direction: column; gap: 10px; }
    }
  `;

  // ═══════════════ Render ═══════════════
  return (
    <div className="gp-page">
      <style>{styles}</style>
      <div className="gp-container">
        <div className="gp-header">
          <div>
            <h1 className="gp-heading">Gerar Provas / Questionários</h1>
            <p className="gp-sub">Crie novos questionários e acompanhe o estado</p>
          </div>
          <button className="gp-btn-add" onClick={openModal}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Gerar Questionário
          </button>
        </div>

        {errorMsg && <div className="gp-error-banner">⚠️ {errorMsg}</div>}
        {successMsg && <div className="gp-success-banner">✅ {successMsg}</div>}

        {/* Filtros da listagem */}
        <div className="gp-toolbar">
          <div className="gp-search-box">
            <span style={{ color: t.textMuted }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              placeholder="Pesquisar por utilizador..."
              value={filterUsuario}
              onChange={(e) => setFilterUsuario(e.target.value)}
            />
          </div>
          <div className="gp-search-box">
            <span style={{ color: t.textMuted }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            <input
              placeholder="Pesquisar por formação..."
              value={filterFormacao}
              onChange={(e) => setFilterFormacao(e.target.value)}
            />
          </div>
        </div>

        {/* Tabela de questionários */}
        {loadingData ? (
          <div style={{ textAlign: "center", padding: 60, color: t.textMuted }}>
            <div className="mini-spinner" style={{ borderColor: t.border, borderTopColor: t.textActive, width: 28, height: 28 }} />
            <div style={{ marginTop: 12 }}>Carregando questionários...</div>
          </div>
        ) : filteredQuestionarios.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: t.textMuted }}>
            Nenhum questionário encontrado.
          </div>
        ) : (
          <div className="gp-table-card">
            <table className="gp-table">
              <thead>
                <tr>
                  <th>Utilizador</th>
                  <th>Departamento</th>
                  <th>Formação</th>
                  <th>Status</th>
                  <th>Data Início</th>
                  <th>Data Fim</th>
                  <th>Duração</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((q, index) => (
                  <tr key={`${q.nome}-${q.formacao}-${index}`}>
                    <td style={{ fontWeight: 600 }}>{q.nome || "—"}</td>
                    <td>{q.departamento || "—"}</td>
                    <td>{q.formacao || "—"}</td>
                    <td>
                      <span
                        className={`gp-badge ${
                          q.status === "Concluído"
                            ? "concluido"
                            : q.status === "Pendente"
                            ? "pendente"
                            : "emcurso"
                        }`}
                      >
                        {q.status || "—"}
                      </span>
                    </td>
                    <td>{formatDate(q.data_inicio)}</td>
                    <td>{formatDate(q.data_fim)}</td>
                    <td>{q.duracao || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            <div className="gp-pagination">
              <span>
                {paginated.length} de {filteredQuestionarios.length}
              </span>
              <div className="gp-pagination-controls">
                <button
                  className="gp-pagination-btn"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={`gp-pagination-num ${num === safePage ? "active" : ""}`}
                    onClick={() => setPage(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  className="gp-pagination-btn"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal para gerar questionário */}
      {showModal && (
        <div
          className="gp-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="gp-modal">
            <div className="gp-modal-header">
              <div className="gp-modal-title">Gerar Novo Questionário</div>
              <button className="gp-modal-close" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="gp-modal-body">
              {errorMsg && (
                <div className="gp-error-banner" style={{ marginBottom: 0 }}>
                  ⚠️ {errorMsg}
                </div>
              )}

       <div className="gp-field">
  <label>Utilizador *</label>

 <Select
  className="gp-ant-select"
  popupClassName="gp-ant-select-dropdown"
  showSearch
  allowClear
  placeholder="Selecione um utilizador..."
  value={selectedUsuarioId || undefined}
  onChange={(value) => setSelectedUsuarioId(value || "")}
  optionFilterProp="label"
  options={usuarios.map((u) => ({
    value: u.id,
    label: `${u.nome}${u.departamento ? ` (${u.departamento})` : ""}`,
  }))}
  filterOption={(input, option) =>
    option?.label?.toLowerCase().includes(input.toLowerCase())
  }
/>
</div>

             <div className="gp-field">
  <label>Formação *</label>

<Select
  className="gp-ant-select"
  popupClassName="gp-ant-select-dropdown"
  showSearch
  allowClear
  placeholder="Selecione uma formação..."
  value={selectedFormacaoId || undefined}
  onChange={(value) => setSelectedFormacaoId(value || "")}
  optionFilterProp="label"
  options={formacoes.map((f) => ({
    value: f.id,
    label: f.titulo,
  }))}
  filterOption={(input, option) =>
    option?.label?.toLowerCase().includes(input.toLowerCase())
  }
/>
</div>
            </div>

            <div className="gp-modal-footer">
              <button className="gp-btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button
                className="gp-btn-primary"
                onClick={handleGerarQuestionario}
                disabled={gerando || !selectedUsuarioId || !selectedFormacaoId}
              >
                {gerando ? <span className="mini-spinner" /> : "Gerar Prova"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}